import React, { useState } from 'react'
import { useDrag, useDrop, UseDropProps } from '../../utils'
import {
  SelectDropdownLinkItem,
  SelectDropdownLinkItemProps,
} from '@sketch/components'

interface DroppableLinkItemProps
  extends SelectDropdownLinkItemProps,
    UseDropProps {
  forceHighlight?: boolean
  isDroppableAreaEnabled?: boolean
  projectIdentifier?: string
}

const useEnableDroppableArea = ({
  isDroppableAreaEnabled,
  onStorageItemsDragOver,
  onUnknownDragOver,
  projectIdentifier,
}: Pick<
  DroppableLinkItemProps,
  | 'isDroppableAreaEnabled'
  | 'onStorageItemsDragOver'
  | 'onUnknownDragOver'
  | 'projectIdentifier'
>) => {
  const [enableDroppableArea, setEnableDroppableArea] = useState(false)

  const onStorageItemsDragOverHandler: DroppableLinkItemProps['onStorageItemsDragOver'] =
    (draggedItem, relatableItems) => {
      if (
        relatableItems.find(
          ({ identifier }) => identifier === projectIdentifier
        )
      ) {
        // Prevent enabling droppable area when dragging over itself
        return
      }

      if (onStorageItemsDragOver) {
        onStorageItemsDragOver(draggedItem, relatableItems)
      } else {
        setEnableDroppableArea(true)
      }
    }

  const onUnknownDragOverHandler: DroppableLinkItemProps['onUnknownDragOver'] =
    event => {
      if (onUnknownDragOver) {
        onUnknownDragOver(event)
      } else {
        setEnableDroppableArea(false)
      }
    }

  return {
    enableDroppableArea: isDroppableAreaEnabled || enableDroppableArea,
    onStorageItemsDragOverHandler,
    onUnknownDragOverHandler,
  }
}

export const DroppableLinkItem = ({
  ref,
  ...props
}: DroppableLinkItemProps & {
  ref?: React.RefObject<unknown | null>
}) => {
  const {
    forceHighlight = false,
    onStorageItemsDragOver,
    onStorageItemsDropped,
    onUnknownDragOver,
    isDroppableAreaEnabled,
    projectIdentifier,
    draggable = false,
    ...linkProps
  } = props

  /**
   * For folders in the sidebar we want to manage these handlers in
   * ProjectListItem and pass them as props, because there we have all the
   * info needed to decide if a droppable area should be enabled or not.
   *
   * But for other situations, like Trash, we just want to enable the
   * droppable area if the user is dragging something *we know* over it
   *  (a share or a folder).
   */
  const {
    onStorageItemsDragOverHandler,
    onUnknownDragOverHandler,
    enableDroppableArea,
  } = useEnableDroppableArea({
    isDroppableAreaEnabled,
    onStorageItemsDragOver,
    onUnknownDragOver,
    projectIdentifier,
  })

  const dragEvents = useDrag({
    item: { __typename: 'Project', identifier: projectIdentifier! },
    relatedItems: [],
    customDragImage: true,
    customDragImageStyles: {
      transform: 'scale(0.8)',
    },
  })

  const { isDraggedOver, kind, isDraggedOverItself, ...dropEvents } = useDrop({
    onStorageItemsDropped: (draggedElement, relatableItems) => {
      if (
        relatableItems.find(
          ({ identifier }) => identifier === projectIdentifier
        )
      ) {
        // Prevent enabling droppable area when dragging over itself
        return
      }

      onStorageItemsDropped?.(draggedElement, relatableItems)
    },
    onStorageItemsDragOver: onStorageItemsDragOverHandler,
    onUnknownDragOver: onUnknownDragOverHandler,
  })

  const shouldForceHover =
    enableDroppableArea && isDraggedOver && !isDraggedOverItself

  return (
    <SelectDropdownLinkItem
      forceHover={shouldForceHover}
      forceActive={forceHighlight}
      ref={ref}
      {...linkProps}
      draggable={draggable}
      // `draggable` already implies that `projectIdentifier` is defined
      // (see ProjectListItem), but just to be safe we double check here
      {...(draggable && projectIdentifier ? dragEvents : {})}
      {...dropEvents}
    />
  )
}
